import { memo, useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import FormEditor from '@/components/Form/components/Editor';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';

import { suggestionsContainerStyles } from '../../../SuggestionsContainer/styles';
import { AIBaseEditorProps, AIFormEditorProps } from '../../types';
import AIBaseEditor from '../AIBaseEditor';

function AIFormEditor({
  text,
  medias,
  setFieldValue,
  textFieldKey,
  mediasFieldKey,
  isTextAndMediaIndependent,
  placeholder,
  className,
  variant = 'body-short-01',
  topic,
  textUseCase,
  disabled,
  setNudgeState,
  nudgeKey,
}: AIFormEditorProps) {
  const { [FeatureFlags.EnableLyearnAI]: enableLyearnAI } = useFeatureFlags();
  const { t } = useTranslation();

  const onTopicError = useCallback(() => {
    setNudgeState(nudgeKey, t('AI:YouNeedTitleForAiToWork'));
  }, [nudgeKey, setNudgeState, t]);

  const onChangeText = useCallback(
    (nextValue: string) => {
      setFieldValue(
        textFieldKey,
        isTextAndMediaIndependent ? nextValue : { text: nextValue, medias },
        true,
      );
    },
    [isTextAndMediaIndependent, medias, setFieldValue, textFieldKey],
  );

  const onChangeMedias = useCallback(
    (nextValue: any[]) => {
      if (mediasFieldKey)
        setFieldValue(
          mediasFieldKey,
          isTextAndMediaIndependent ? nextValue : { text, medias: nextValue },
          true,
        );
    },
    [mediasFieldKey, setFieldValue, isTextAndMediaIndependent, text],
  );

  const EDITOR_CLASSES = useMemo<AIBaseEditorProps['classes']>(
    () => ({
      root: 'rounded-8 border border-field focus-within:border-focus-primary',
      rootActiveAI: cn(
        'border-dashed border-bg-primary focus-within:!border-bg-primary',
        suggestionsContainerStyles,
      ),
    }),
    [],
  );

  const EDITOR_CORE_PROPS = useMemo<AIBaseEditorProps['editorCoreProps']>(
    () => ({ classes: { typography: '!max-h-[28.1rem]' }, variant }),
    [variant],
  );

  if (enableLyearnAI) {
    return (
      <AIBaseEditor
        className={className}
        classes={EDITOR_CLASSES}
        disabled={disabled}
        editorCoreProps={EDITOR_CORE_PROPS}
        medias={medias}
        placeholder={placeholder}
        textUseCase={textUseCase}
        topic={topic}
        value={text}
        onChangeMedias={mediasFieldKey ? onChangeMedias : undefined}
        onChangeText={onChangeText}
        onTopicError={onTopicError}
      />
    );
  }

  return (
    <FormEditor
      {...{
        disabled,
        text,
        medias,
        setFieldValue,
        textFieldKey,
        mediasFieldKey,
        isTextAndMediaIndependent,
        placeholder,
        className,
        variant,
      }}
    />
  );
}

export default memo(AIFormEditor);
